import React, { useState, useRef, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const ProjectCard = ({ project, onOpen, onRename, onDownload, onShare, onDelete, isSelected, onSelect, memberName, isOwnerView }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(project?.name || '');
  const inputRef = useRef(null);

  const getStatusColor = (status) => {
    const colorMap = {
      completed: 'bg-success/10 text-success',
      'in-progress': 'bg-warning/10 text-warning',
      draft: 'bg-secondary/10 text-secondary'
    };
    return colorMap?.[status] || 'bg-muted text-muted-foreground';
  };

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const handleRenameSubmit = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== project?.name) {
      onRename?.(project?.id, trimmed);
    } else {
      setRenameValue(project?.name || '');
    }
    setIsRenaming(false);
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      setRenameValue(project?.name || '');
      setIsRenaming(false);
    }
  };

  return (
    <div
      className="glassmorphic-card rounded-2xl p-5 transition-all duration-300"
      style={{
        border: isSelected
          ? '1px solid rgba(254, 131, 56, 0.4)'
          : '1px solid rgba(255, 255, 255, 0.06)',
      }}
    >
      {/* Owner View: Member Badge */}
      {isOwnerView && memberName && (
        <div className="flex items-center gap-1.5 mb-2">
          <Icon name="User" size={12} className={project?.isOwnProject ? 'text-orange-400' : 'text-yellow-300'} />
          <span className={`text-xs font-medium ${project?.isOwnProject ? 'text-orange-400' : 'text-yellow-300'}`}>
            {memberName}
          </span>
        </div>
      )}

      {/* Header: Name + Rename */}
      <div className="flex items-start justify-between mb-3">
        {isRenaming ? (
          <input
            ref={inputRef}
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={handleRenameKeyDown}
            className="text-lg font-bold text-white bg-white/[0.05] border border-[rgba(254,131,56,0.3)] rounded-lg px-3 py-1 flex-1 mr-3 focus:outline-none focus:border-[rgba(254,131,56,0.5)] focus:shadow-[0_0_12px_rgba(254,131,56,0.15)]"
          />
        ) : (
          <h3
            className="text-lg font-bold text-white cursor-pointer hover:text-orange-400 transition-colors truncate flex-1 mr-3"
            onClick={() => onOpen(project?.id)}
          >
            {project?.name}
          </h3>
        )}
        {onRename && (
          <button
            onClick={() => {
              if (isRenaming) {
                handleRenameSubmit();
              } else {
                setRenameValue(project?.name || '');
                setIsRenaming(true);
              }
            }}
            className={`flex-shrink-0 transition-colors ${isRenaming ? 'text-orange-400' : 'text-white/30 hover:text-orange-400'}`}
            aria-label="Rename project"
          >
            <Icon name={isRenaming ? 'Check' : 'Pencil'} size={18} />
          </button>
        )}
      </div>

      {/* Used Tools */}
      <p className="text-xs text-white/50 font-medium mb-2">Used tools:</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <span
          className="px-3 py-1.5 rounded-lg text-xs font-medium text-white/80"
          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          {project?.toolName}
        </span>
      </div>

      {/* Divider */}
      <div className="mb-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }} />

      {/* Generated Images Expandable */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm text-white/50 hover:text-white/70 transition-colors mb-3"
      >
        <Icon name={isExpanded ? 'ChevronDown' : 'ChevronRight'} size={16} />
        <span>{project?.designsCount ?? 0} generated images</span>
      </button>

      {/* Expanded: show thumbnail previews */}
      {isExpanded && project?.thumbnail && (
        <div className="mb-3 rounded-xl overflow-hidden cursor-pointer" onClick={() => onOpen(project?.id)}>
          <Image
            src={project?.thumbnail}
            alt={project?.thumbnailAlt}
            className="w-full h-40 object-cover rounded-xl hover:scale-[1.02] transition-transform duration-300"
          />
        </div>
      )}

      {/* Delete */}
      <div className="flex justify-end">
        {onDelete ? (
          <button
            onClick={() => onDelete(project?.id)}
            className="flex items-center gap-2 text-xs text-white/30 hover:text-red-400 transition-colors"
          >
            <Icon name="Trash2" size={14} />
            <span>Delete project</span>
          </button>
        ) : (
          <span className="text-xs text-white/20 italic">View only</span>
        )}
      </div>
    </div>
  );
};

export default ProjectCard;
